import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Users, ChevronDown, ChevronRight, Check, Clock, MapPin, UserCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Coworker {
  id: string;
  display_name: string;
  team: string | null;
  position?: string | null;
}

interface SmartCoworkerPickerProps {
  companyId: string;
  currentWorkerId: string;
  siteId?: string | null;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

interface RecentCoworker {
  workerId: string;
  count: number;
}

export const SmartCoworkerPicker = ({
  companyId,
  currentWorkerId,
  siteId,
  selectedIds,
  onSelectionChange,
}: SmartCoworkerPickerProps) => {
  const [allCoworkers, setAllCoworkers] = useState<Coworker[]>([]);
  const [recentCoworkerIds, setRecentCoworkerIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set(["recent"]));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCoworkersAndRecent();
  }, [companyId, currentWorkerId, siteId]);

  const loadCoworkersAndRecent = async () => {
    setIsLoading(true);
    try {
      // Load all coworkers
      let query = supabase
        .from("worker_profiles")
        .select("id, display_name, team, position")
        .eq("company_id", companyId)
        .neq("id", currentWorkerId);

      if (siteId) {
        query = query.eq("site_id", siteId);
      }

      const { data: coworkers } = await query.order("display_name");

      if (coworkers) {
        setAllCoworkers(coworkers);
      }

      // Load recent coworkers (last 7 days of check-ins)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data: recentCheckins } = await supabase
        .from("shift_checkins")
        .select("working_with")
        .eq("worker_id", currentWorkerId)
        .gte("check_in_date", sevenDaysAgo.toISOString().split("T")[0])
        .order("check_in_date", { ascending: false })
        .limit(10);

      if (recentCheckins) {
        // Flatten and count recent coworker occurrences
        const recentCounts: Record<string, number> = {};
        recentCheckins.forEach((checkin) => {
          const workingWith = checkin.working_with as string[] | null;
          if (workingWith) {
            workingWith.forEach((id) => {
              recentCounts[id] = (recentCounts[id] || 0) + 1;
            });
          }
        });

        // Sort by frequency and take top 5
        const sorted = Object.entries(recentCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([id]) => id);

        setRecentCoworkerIds(sorted);
      }
    } catch (error) {
      console.error("Error loading coworkers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Group coworkers by team
  const groupedCoworkers = useMemo(() => {
    const groups: Record<string, Coworker[]> = {};
    const noTeam: Coworker[] = [];

    allCoworkers.forEach((cw) => {
      if (cw.team) {
        if (!groups[cw.team]) {
          groups[cw.team] = [];
        }
        groups[cw.team].push(cw);
      } else {
        noTeam.push(cw);
      }
    });

    if (noTeam.length > 0) {
      groups["Lainnya"] = noTeam;
    }

    return groups;
  }, [allCoworkers]);

  // Filter coworkers by search
  const filteredCoworkers = useMemo(() => {
    if (!searchQuery.trim()) return allCoworkers;
    const query = searchQuery.toLowerCase();
    return allCoworkers.filter(
      (cw) =>
        cw.display_name?.toLowerCase().includes(query) ||
        cw.team?.toLowerCase().includes(query)
    );
  }, [allCoworkers, searchQuery]);

  // Recent coworkers (filtered)
  const recentCoworkers = useMemo(() => {
    return allCoworkers.filter((cw) => recentCoworkerIds.includes(cw.id));
  }, [allCoworkers, recentCoworkerIds]);

  const toggleCoworker = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const toggleTeam = (teamName: string) => {
    const teamMembers = groupedCoworkers[teamName] || [];
    const allSelected = teamMembers.every((m) => selectedIds.includes(m.id));

    if (allSelected) {
      // Deselect all team members
      onSelectionChange(
        selectedIds.filter((id) => !teamMembers.some((m) => m.id === id))
      );
    } else {
      // Select all team members
      const newIds = new Set(selectedIds);
      teamMembers.forEach((m) => newIds.add(m.id));
      onSelectionChange(Array.from(newIds));
    }
  };

  const toggleExpand = (teamName: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamName)) {
      newExpanded.delete(teamName);
    } else {
      newExpanded.add(teamName);
    }
    setExpandedTeams(newExpanded);
  };

  const CoworkerRow = ({ cw, showTeam = false }: { cw: Coworker; showTeam?: boolean }) => (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all",
        selectedIds.includes(cw.id)
          ? "bg-primary/10 border border-primary/20"
          : "bg-background/60 hover:bg-muted/50 border border-transparent"
      )}
      onClick={() => toggleCoworker(cw.id)}
    >
      <Checkbox
        checked={selectedIds.includes(cw.id)}
        className="pointer-events-none"
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium block truncate">
          {cw.display_name}
        </span>
        {showTeam && cw.team && (
          <span className="text-xs text-muted-foreground">{cw.team}</span>
        )}
      </div>
      {selectedIds.includes(cw.id) && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </motion.div>
  );

  const TeamSection = ({ teamName, members }: { teamName: string; members: Coworker[] }) => {
    const isExpanded = expandedTeams.has(teamName);
    const selectedCount = members.filter((m) => selectedIds.includes(m.id)).length;
    const allSelected = members.length > 0 && selectedCount === members.length;

    return (
      <div className="border border-border/50 rounded-xl overflow-hidden">
        <div
          className="flex items-center gap-2 p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => toggleExpand(teamName)}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium text-sm flex-1">{teamName}</span>
          <Badge variant="secondary" className="text-xs">
            {members.length}
          </Badge>
          {selectedCount > 0 && (
            <Badge variant="default" className="text-xs">
              {selectedCount} dipilih
            </Badge>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleTeam(teamName);
            }}
            className={cn(
              "text-xs px-2 py-1 rounded-lg transition-colors",
              allSelected
                ? "bg-primary/20 text-primary hover:bg-primary/30"
                : "bg-muted hover:bg-muted/80 text-muted-foreground"
            )}
          >
            {allSelected ? "Batal semua" : "Pilih semua"}
          </button>
        </div>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-2 space-y-1 max-h-48 overflow-y-auto">
                {members.map((cw) => (
                  <CoworkerRow key={cw.id} cw={cw} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-muted/40 p-4 animate-pulse">
        <div className="h-8 bg-muted rounded-lg mb-3" />
        <div className="space-y-2">
          <div className="h-12 bg-muted rounded-xl" />
          <div className="h-12 bg-muted rounded-xl" />
          <div className="h-12 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (allCoworkers.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl bg-muted/40 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
            <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h4 className="font-medium text-sm">Rekan Kerja</h4>
            <p className="text-xs text-muted-foreground">
              Pilih siapa saja yang bekerja denganmu
            </p>
          </div>
        </div>
        {selectedIds.length > 0 && (
          <Badge className="bg-primary">{selectedIds.length} dipilih</Badge>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cari nama atau tim..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-10 pl-9 rounded-xl bg-background border-border/50"
        />
      </div>

      {/* Content */}
      {searchQuery.trim() ? (
        // Search Results
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {filteredCoworkers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Tidak ditemukan
            </p>
          ) : (
            filteredCoworkers.map((cw) => (
              <CoworkerRow key={cw.id} cw={cw} showTeam />
            ))
          )}
        </div>
      ) : (
        // Grouped View
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {/* Recent Section */}
          {recentCoworkers.length > 0 && (
            <div className="border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 rounded-xl overflow-hidden">
              <div
                className="flex items-center gap-2 p-3 cursor-pointer"
                onClick={() => toggleExpand("recent")}
              >
                {expandedTeams.has("recent") ? (
                  <ChevronDown className="h-4 w-4 text-amber-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-amber-600" />
                )}
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-sm flex-1 text-amber-800 dark:text-amber-200">
                  Terakhir bekerja bersama
                </span>
                <Badge
                  variant="secondary"
                  className="bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300"
                >
                  {recentCoworkers.length}
                </Badge>
              </div>
              <AnimatePresence>
                {expandedTeams.has("recent") && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-2 space-y-1">
                      {recentCoworkers.map((cw) => (
                        <CoworkerRow key={cw.id} cw={cw} showTeam />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Team Sections */}
          {Object.entries(groupedCoworkers)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([teamName, members]) => (
              <TeamSection key={teamName} teamName={teamName} members={members} />
            ))}
        </div>
      )}
    </div>
  );
};

export default SmartCoworkerPicker;
